import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/lib/site";
import { eventsApi, pence } from "@/lib/eventsApi";

/** Stripe sends the buyer back here: ?session_id=… (poll the order) or ?cancelled=1&order_id=… */
export default function OrderReturn() {
  usePageMeta("Your tickets", "Order confirmation.");
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const cancelled = params.get("cancelled") === "1";
  const free = params.get("free") === "1";
  const orderId = params.get("order_id");
  const eventId = params.get("event_id");
  const [state, setState] = useState(cancelled ? "cancelled" : free ? "paid" : "checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (cancelled && orderId) eventsApi.abandonOrder(orderId).catch(() => null);
  }, [cancelled, orderId]);

  useEffect(() => {
    if (!sid) return undefined;
    let tries = 0;
    let alive = true;
    const poll = async () => {
      try {
        const o = await eventsApi.orderBySession(sid);
        if (!alive) return;
        setOrder(o);
        if (o.status === "paid" || o.status === "authorised" || o.status === "fulfilled") { setState("paid"); return; }
        if (o.status === "abandoned" || o.status === "cancelled") { setState("cancelled"); return; }
      } catch { /* keep polling */ }
      if (++tries < 15 && alive) setTimeout(poll, 2000);
      else if (alive) setState("unknown");
    };
    poll();
    return () => { alive = false; };
  }, [sid]);

  return (
    <div className="mx-auto max-w-lg px-4 py-20 text-center sm:px-6" data-testid="order-return">
      {state === "checking" && (
        <>
          <Loader2 size={36} className="mx-auto animate-spin text-primary" />
          <h1 className="mt-4 font-serif-display text-2xl font-bold">Confirming your order…</h1>
          <p className="mt-2 text-muted-foreground">This usually takes a few seconds.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
          <h1 className="mt-4 font-serif-display text-2xl font-bold">You're going.</h1>
          <p className="mt-2 text-muted-foreground">
            {order ? `${order.items?.reduce((s, i) => s + i.qty, 0) || ""} ticket${(order.items?.reduce((s, i) => s + i.qty, 0) || 0) === 1 ? "" : "s"}${order.total_p ? ` · ${pence(order.total_p)}` : " · free"} · ref ${order.ref || ""}` : "Your tickets are ready."}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/tickets"><Button className="rounded-full px-6">My tickets</Button></Link>
            <Link to="/events"><Button variant="outline" className="rounded-full">More events</Button></Link>
          </div>
        </>
      )}
      {state === "cancelled" && (
        <>
          <XCircle size={40} className="mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-serif-display text-2xl font-bold">No charge was made.</h1>
          <p className="mt-2 text-muted-foreground">Your tickets were released. You can pick them again whenever you're ready.</p>
          <Link to={eventId ? `/events/${eventId}` : "/events"}><Button className="mt-6 rounded-full px-6">Back to the event</Button></Link>
        </>
      )}
      {state === "unknown" && (
        <>
          <h1 className="font-serif-display text-2xl font-bold">Still confirming</h1>
          <p className="mt-2 text-muted-foreground">Your payment is being checked. Your tickets will appear in My tickets as soon as it clears.</p>
          <Link to="/tickets"><Button className="mt-6 rounded-full px-6">My tickets</Button></Link>
        </>
      )}
    </div>
  );
}
